import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Checkbox, DataTable, FormDrawer, IconButton, Input, Modal, Select, Spin, Switch, useAlert } from '../ui';
import { DataTableColumn } from '../ui/DataTable';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import { Coins, FolderTree, Info, Landmark, Link2, ListOrdered, Paperclip, Pencil, Plus, Save, Trash2, Unlink } from 'lucide-react';
import PageHeader from '../ui/PageHeader';
import TableStatusBar from '../ui/TableStatusBar';

/** Entités administrables via POST /api/Operations/ManageCatalogue (dispatch Entite + Option). */
type Entite = 'CATEGORIE' | 'OPERATION' | 'ORIGINE' | 'DENOMINATION' | 'FILIALE_OPERATION';
type TabKey = 'operations' | 'categories' | 'origines' | 'denominations' | 'affectations';

const ENTITE_BY_TAB: Record<TabKey, Entite> = {
    operations: 'OPERATION',
    categories: 'CATEGORIE',
    origines: 'ORIGINE',
    denominations: 'DENOMINATION',
    affectations: 'FILIALE_OPERATION',
};

const TABS: { key: TabKey; label: string; icon: React.ReactNode; hint: string }[] = [
    { key: 'operations', label: 'Opérations', icon: <ListOrdered size={15} />, hint: "Chaque opération est rattachée à un CodeS — c'est ce lien qui route le panier du client vers le bon service/guichet." },
    { key: 'categories', label: 'Catégories', icon: <FolderTree size={15} />, hint: 'Regroupement des opérations (ex. opérations de caisse) pour la navigation dans le catalogue.' },
    { key: 'origines', label: 'Origines de fonds', icon: <Landmark size={15} />, hint: 'Origines déclarables par le client pour les opérations qui exigent une justification de fonds.' },
    { key: 'denominations', label: 'Coupures', icon: <Coins size={15} />, hint: 'Billets et pièces disponibles par devise — utilisés pour le billetage des opérations de caisse.' },
    { key: 'affectations', label: 'Affectations filiales', icon: <Link2 size={15} />, hint: 'Attache/détache des opérations aux filiales — une opération non affectée n\'est pas proposée dans la filiale.' },
];

/** Retourne la première propriété non nulle parmi plusieurs noms possibles. */
function pick(obj: any, ...keys: string[]): any {
    for (const k of keys) {
        if (obj?.[k] !== undefined && obj?.[k] !== null) return obj[k];
    }
    return undefined;
}

/** Formate une valeur numérique avec séparateurs fr-FR. */
function formatNombre(v: any): string {
    const n = Number(v)
    if (v == null || isNaN(n)) return v == null ? '—' : String(v)
    return n.toLocaleString('fr-FR')
}

/** Extrait la liste de lignes depuis la réponse déchiffrée (tableau direct ou {Table}/{Table1}). */
function toList(res: any): any[] {
    if (!res) return [];
    const r = res.Result ?? res;
    if (Array.isArray(r)) return r;
    if (Array.isArray(r?.Table)) return r.Table;
    if (Array.isArray(r?.Table1)) return r.Table1;
    return [];
}

const PREREQUIS_FLAGS: { field: string; label: string; color: 'violet' | 'amber' | 'blue' | 'green' | 'red'; hint: string }[] = [
    { field: 'NecessiteKYC', label: 'KYC', color: 'violet', hint: 'Le formulaire KYC de la filiale est affiché avant l\'opération' },
    { field: 'NecessiteMontant', label: 'Montant', color: 'amber', hint: 'Le client saisit un montant' },
    { field: 'NecessiteOrigineFonds', label: 'Origine fonds', color: 'blue', hint: 'Le client déclare l\'origine des fonds' },
    { field: 'NecessiteBilletage', label: 'Billetage', color: 'green', hint: 'La répartition en coupures est demandée' },
    { field: 'NecessiteRDV', label: 'RDV', color: 'red', hint: 'L\'opération nécessite un rendez-vous' },
];

/**
 * Garantit que la valeur courante du formulaire est présente dans les options
 * d'un Select — sinon le Select affiche « Sélectionner... » alors que le champ
 * a une valeur (ex. CodeS/CodeCategorie absent de la liste chargée en UPDATE).
 */
function withCurrentOption(options: { value: string; label: React.ReactNode }[], value: any) {
    const v = value == null ? '' : String(value);
    if (v && !options.some(o => o.value === v)) {
        return [{ value: v, label: `${v} (hors liste)` }, ...options];
    }
    return options;
}

export default function GestionOperations() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [tab, setTab] = useState<TabKey>('operations')
    const [rows, setRows] = useState<Record<TabKey, any[]>>({
        operations: [],
        categories: [],
        origines: [],
        denominations: [],
        affectations: [],
    })
    const [loaded, setLoaded] = useState<Record<TabKey, boolean>>({
        operations: false,
        categories: false,
        origines: false,
        denominations: false,
        affectations: false,
    })
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    // Référentiels pour les filtres et les formulaires
    const [filiales, setFiliales] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])

    // Filtres client-side (LIST retourne tout le catalogue)
    const [filtreCategorie, setFiltreCategorie] = useState('')
    const [filtreFiliale, setFiltreFiliale] = useState('')
    const [filtreService, setFiltreService] = useState('')
    const [filtreDevise, setFiltreDevise] = useState('')
    // Filtre serveur du LIST FILIALE_OPERATION (CodeFiliale optionnel)
    const [filtreAffectation, setFiltreAffectation] = useState('')

    // Formulaire create/update + confirmation delete/detach
    const [formOpen, setFormOpen] = useState(false)
    const [formEntite, setFormEntite] = useState<Entite>('OPERATION')
    const [formMode, setFormMode] = useState<'CREATE' | 'UPDATE' | 'ATTACH'>('CREATE')
    const [form, setForm] = useState<Record<string, any>>({})
    const [confirm, setConfirm] = useState<{ entite: Entite; record: any } | null>(null)

    // Pièces attendues par opération (droit D12 ou D13)
    const habilitations: any[] = (() => {
        try { return JSON.parse(Crypter.decrypt(localStorage.getItem(datas.Habilitations) || '')) || [] } catch { return [] }
    })()
    const canPieces = ['SUADMIN', 'ADMIN', 'IT'].includes(user.RoleId)
        || habilitations.some((h: any) => h.CodeHabi === 'D12' || h.CodeHabi === 'D13')

    useEffect(() => {
        LoadTab('operations')
        LoadTab('categories')
        LoadFiliales()
        LoadServices()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadFiliales() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() }
        };
        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) setFiliales(res.Result || [])
            })
            .catch((error: any) => console.log(error));
    }

    function LoadServices() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetFilialeServices + Crypter.encrypt(user.CodeAbonne),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() }
        };
        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    const svc = Array.isArray(res.Result?.[1]) ? res.Result[1] : (res.Result || [])
                    setServices(svc)
                }
            })
            .catch((error: any) => console.log(error));
    }

    /** POST /api/Operations/ManageCatalogue — mutations (CREATE/UPDATE/DELETE/ATTACH/DETACH). */
    function postCatalogue(payload: any): Promise<any> {
        return axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Operations.ManageCatalogue,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        }).then((response: any) => JSON.parse(Crypter.decrypt(response.data)).Value);
    }

    /** GET /api/Operations/ManageCatalogue?CipherCatalogue= — lecture seule (LIST/GET). */
    function getCatalogue(payload: any): Promise<any> {
        return axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Operations.ManageCatalogueGet + Crypter.encrypt(JSON.stringify(payload)),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            },
        }).then((response: any) => JSON.parse(Crypter.decrypt(response.data)).Value);
    }

    function switchTab(t: TabKey) {
        setTab(t)
        if (!loaded[t]) LoadTab(t)
    }

    /** LIST via ManageCatalogue GET (lazy — au premier accès de l'onglet). */
    function LoadTab(key: TabKey, filialeOverride?: string) {
        setLoaded(prev => ({ ...prev, [key]: true }))
        setLoading(true)
        const payload: any = { Entite: ENTITE_BY_TAB[key], Option: 'LIST', CodeAbonne: user.CodeAbonne }
        const fAff = filialeOverride !== undefined ? filialeOverride : filtreAffectation
        if (key === 'affectations' && fAff) payload.CodeFiliale = fAff
        getCatalogue(payload)
            .then((res: any) => {
                setLoading(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    setRows(prev => ({ ...prev, [key]: normalizeRows(key, toList(res)) }))
                } else {
                    setRows(prev => ({ ...prev, [key]: [] }))
                    if (res?.Message) alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    /** Normalise les lignes de chaque onglet vers un shape stable pour le DataTable. */
    function normalizeRows(key: TabKey, list: any[]): any[] {
        return list.map((o: any, i: number) => {
            switch (key) {
                case 'operations':
                    return {
                        id: pick(o, 'CodeOperation', 'Code', 'Id') ?? i,
                        CodeOperation: o.CodeOperation,
                        Libelle: o.Libelle,
                        CodeS: o.CodeS,
                        CodeCategorie: o.CodeCategorie,
                        LibelleCategorie: o.LibelleCategorie,
                        NecessiteKYC: !!o.NecessiteKYC,
                        NecessiteMontant: !!o.NecessiteMontant,
                        NecessiteOrigineFonds: !!o.NecessiteOrigineFonds,
                        NecessiteBilletage: !!o.NecessiteBilletage,
                        NecessiteRDV: !!o.NecessiteRDV,
                    }
                case 'categories':
                    return { id: o.CodeCategorie ?? i, CodeCategorie: o.CodeCategorie, Libelle: o.Libelle }
                case 'origines':
                    return { id: o.CodeOrigine ?? i, CodeOrigine: o.CodeOrigine, Libelle: o.Libelle }
                case 'denominations':
                    return {
                        id: `${o.CodeDevise ?? ''}-${o.TypeCoupure ?? ''}-${o.Valeur ?? i}`,
                        CodeDevise: o.CodeDevise, TypeCoupure: o.TypeCoupure, Valeur: o.Valeur,
                    }
                case 'affectations':
                    return {
                        id: pick(o, 'Id', 'id') ?? `${o.CodeFiliale ?? ''}-${o.CodeOperation ?? i}`,
                        Id: pick(o, 'Id', 'id'),
                        CodeFiliale: o.CodeFiliale,
                        LibelleFiliale: pick(o, 'LibelleFiliale', 'Filiale'),
                        CodeOperation: o.CodeOperation,
                        LibelleOperation: pick(o, 'LibelleOperation', 'Operation'),
                    }
                default:
                    return { id: i, ...o }
            }
        })
    }

    /* ------------------------------------------------------------------ */
    /*  CRUD                                                               */
    /* ------------------------------------------------------------------ */

    /** Ouvre le formulaire de création pour l'entité de l'onglet courant. */
    function openCreate(t: TabKey) {
        setFormEntite(ENTITE_BY_TAB[t])
        setFormMode('CREATE')
        setForm(defaultsFor(ENTITE_BY_TAB[t]))
        setFormOpen(true)
    }

    /** Ouvre le formulaire d'édition pré-rempli (code en lecture seule = clé). */
    function openEdit(t: TabKey, row: any) {
        setFormEntite(ENTITE_BY_TAB[t])
        setFormMode('UPDATE')
        setForm({ ...row })
        setFormOpen(true)
    }

    /** Ouvre le formulaire ATTACH pour FILIALE_OPERATION. */
    function openAttach() {
        setFormEntite('FILIALE_OPERATION')
        setFormMode('ATTACH')
        setForm({ CodeFiliale: '', CodeOperation: '' })
        setFormOpen(true)
    }

    /** Valeurs initiales du formulaire selon l'entité. */
    function defaultsFor(entite: Entite): Record<string, any> {
        switch (entite) {
            case 'OPERATION': return { CodeOperation: '', Libelle: '', CodeCategorie: '', CodeS: '', NecessiteKYC: false, NecessiteMontant: false, NecessiteOrigineFonds: false, NecessiteBilletage: false, NecessiteRDV: false }
            case 'CATEGORIE': return { CodeCategorie: '', Libelle: '' }
            case 'ORIGINE': return { CodeOrigine: '', Libelle: '' }
            case 'DENOMINATION': return { CodeDevise: '', TypeCoupure: 'BILLET', Valeur: '' }
            case 'FILIALE_OPERATION': return { CodeFiliale: '', CodeOperation: '' }
        }
    }

    /** Construit le CatalogueRequest pour le formulaire courant. */
    function buildFormPayload(): any {
        const base: any = { Entite: formEntite, Option: formMode, CodeAbonne: user.CodeAbonne }
        switch (formEntite) {
            case 'OPERATION':
                return {
                    ...base,
                    CodeOperation: form.CodeOperation, Libelle: form.Libelle,
                    CodeCategorie: form.CodeCategorie || undefined, CodeS: form.CodeS || undefined,
                    NecessiteKYC: !!form.NecessiteKYC, NecessiteMontant: !!form.NecessiteMontant,
                    NecessiteOrigineFonds: !!form.NecessiteOrigineFonds, NecessiteBilletage: !!form.NecessiteBilletage,
                    NecessiteRDV: !!form.NecessiteRDV,
                }
            case 'CATEGORIE':
                return { ...base, CodeCategorie: form.CodeCategorie, Libelle: form.Libelle }
            case 'ORIGINE':
                return { ...base, CodeOrigine: form.CodeOrigine, Libelle: form.Libelle }
            case 'DENOMINATION':
                return { ...base, CodeDevise: form.CodeDevise, TypeCoupure: form.TypeCoupure, Valeur: Number(form.Valeur) }
            case 'FILIALE_OPERATION':
                return { ...base, CodeFiliale: form.CodeFiliale, CodeOperation: form.CodeOperation }
        }
    }

    /** Validation minimale côté client avant envoi. */
    function formValid(): boolean {
        switch (formEntite) {
            case 'OPERATION': return !!(form.CodeOperation && form.Libelle)
            case 'CATEGORIE': return !!(form.CodeCategorie && form.Libelle)
            case 'ORIGINE': return !!(form.CodeOrigine && form.Libelle)
            case 'DENOMINATION': return !!(form.CodeDevise && form.TypeCoupure && form.Valeur !== '' && !isNaN(Number(form.Valeur)))
            case 'FILIALE_OPERATION': return !!(form.CodeFiliale && form.CodeOperation)
        }
    }

    function SaveForm() {
        setSaving(true)
        postCatalogue(buildFormPayload())
            .then((res: any) => {
                setSaving(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    alert.success(res?.Message || 'Enregistré')
                    setFormOpen(false)
                    LoadTab(tab)
                    // Les listes de catégories/… alimentent aussi les selects de l'onglet Opérations
                    if (tab === 'affectations') LoadTab('operations')
                } else {
                    alert.warning(res?.Message || 'Enregistrement refusé')
                }
            })
            .catch((error: any) => {
                setSaving(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    /** Construit le CatalogueRequest de suppression/détachement pour une ligne. */
    function buildDeletePayload(c: { entite: Entite; record: any }): any {
        const r = c.record
        const option = c.entite === 'FILIALE_OPERATION' ? 'DETACH' : 'DELETE'
        const base: any = { Entite: c.entite, Option: option, CodeAbonne: user.CodeAbonne }
        switch (c.entite) {
            case 'CATEGORIE': return { ...base, CodeCategorie: r.CodeCategorie }
            case 'OPERATION': return { ...base, CodeOperation: r.CodeOperation }
            case 'ORIGINE': return { ...base, CodeOrigine: r.CodeOrigine }
            case 'DENOMINATION': return { ...base, CodeDevise: r.CodeDevise, TypeCoupure: r.TypeCoupure, Valeur: Number(r.Valeur) }
            case 'FILIALE_OPERATION': return { ...base, CodeFiliale: r.CodeFiliale, CodeOperation: r.CodeOperation, ...(r.Id != null ? { Id: r.Id } : {}) }
        }
    }

    function ConfirmDelete() {
        if (!confirm) return
        setSaving(true)
        postCatalogue(buildDeletePayload(confirm))
            .then((res: any) => {
                setSaving(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    alert.success(res?.Message || 'Supprimé')
                    setConfirm(null)
                    LoadTab(tab)
                } else {
                    alert.warning(res?.Message || 'Suppression refusée')
                }
            })
            .catch((error: any) => {
                setSaving(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    /* ------------------------------------------------------------------ */
    /*  Colonnes                                                           */
    /* ------------------------------------------------------------------ */

    const actionsCol = (t: TabKey, canEdit: boolean): DataTableColumn => ({
        field: '__actions', headerName: 'Actions', width: 90, sortable: false,
        renderCell: ({ row }) => (
            <div className="flex items-center gap-1">
                {canEdit && (
                    <IconButton size={30} color="primary" tooltip="Modifier" onClick={() => openEdit(t, row)}>
                        <Pencil size={14} />
                    </IconButton>
                )}
                <IconButton size={30} color="danger" tooltip={t === 'affectations' ? 'Détacher' : 'Supprimer'} onClick={() => setConfirm({ entite: ENTITE_BY_TAB[t], record: row })}>
                    {t === 'affectations' ? <Unlink size={14} /> : <Trash2 size={14} />}
                </IconButton>
            </div>
        ),
    })

    const columnsOperations: DataTableColumn[] = [
        { field: 'CodeOperation', headerName: 'Code', width: 110 },
        { field: 'Libelle', headerName: 'Libellé', width: 220 },
        {
            field: 'LibelleCategorie', headerName: 'Catégorie', width: 160,
            renderCell: ({ value, row }) => <span>{value ?? row.CodeCategorie ?? '—'}</span>,
        },
        { field: 'CodeS', headerName: 'Service', width: 100 },
        {
            field: 'Prerequis', headerName: 'Prérequis', width: 280, sortable: false,
            renderCell: ({ row }) => {
                const actifs = PREREQUIS_FLAGS.filter(f => !!row[f.field])
                if (actifs.length === 0) return <span className="text-xs text-[var(--eb-text-muted)]">—</span>
                return (
                    <div className="flex flex-wrap items-center gap-1">
                        {actifs.map(f => <Badge key={f.field} color={f.color}>{f.label}</Badge>)}
                    </div>
                )
            },
        },
        actionsCol('operations', true),
    ];

    const columnsCategories: DataTableColumn[] = [
        { field: 'CodeCategorie', headerName: 'Code', width: 140 },
        { field: 'Libelle', headerName: 'Libellé', width: 280 },
        actionsCol('categories', true),
    ];

    const columnsOrigines: DataTableColumn[] = [
        { field: 'CodeOrigine', headerName: 'Code', width: 140 },
        { field: 'Libelle', headerName: 'Libellé', width: 280 },
        actionsCol('origines', true),
    ];

    // DENOMINATION : pas d'UPDATE côté serveur (LIST|CREATE|DELETE) → suppression seule
    const columnsDenominations: DataTableColumn[] = [
        { field: 'CodeDevise', headerName: 'Devise', width: 110 },
        {
            field: 'TypeCoupure', headerName: 'Type', width: 120,
            renderCell: ({ value }) => value
                ? <Badge color={String(value).toUpperCase() === 'BILLET' ? 'blue' : 'amber'}>{String(value)}</Badge>
                : <span>—</span>,
        },
        {
            field: 'Valeur', headerName: 'Valeur', width: 130,
            renderCell: ({ value }) => <span>{formatNombre(value)}</span>,
        },
        actionsCol('denominations', false),
    ];

    const columnsAffectations: DataTableColumn[] = [
        { field: 'CodeFiliale', headerName: 'Filiale', width: 120 },
        {
            field: 'LibelleFiliale', headerName: 'Libellé filiale', width: 180,
            renderCell: ({ value }) => <span>{value ?? '—'}</span>,
        },
        { field: 'CodeOperation', headerName: 'Opération', width: 130 },
        {
            field: 'LibelleOperation', headerName: 'Libellé opération', width: 200,
            renderCell: ({ value, row }) => <span>{value ?? rows.operations.find((o: any) => o.CodeOperation === row.CodeOperation)?.Libelle ?? '—'}</span>,
        },
        actionsCol('affectations', false),
    ];

    const columnsByTab: Record<TabKey, DataTableColumn[]> = {
        operations: columnsOperations,
        categories: columnsCategories,
        origines: columnsOrigines,
        denominations: columnsDenominations,
        affectations: columnsAffectations,
    };

    const emptyTextByTab: Record<TabKey, string> = {
        operations: 'Aucune opération',
        categories: 'Aucune catégorie',
        origines: 'Aucune origine de fonds',
        denominations: 'Aucune coupure',
        affectations: 'Aucune affectation',
    };

    const newLabelByTab: Record<TabKey, string> = {
        operations: 'Nouvelle opération',
        categories: 'Nouvelle catégorie',
        origines: 'Nouvelle origine',
        denominations: 'Nouvelle coupure',
        affectations: 'Affecter à une filiale',
    };

    /* ------------------------------------------------------------------ */
    /*  Filtres client-side (LIST retourne tout)                           */
    /* ------------------------------------------------------------------ */

    const opsFiltrees = useMemo(() => {
        const svcOfFiliale = new Set(
            services.filter((s: any) => !filtreFiliale || s.CodeFiliale == filtreFiliale).map((s: any) => s.CodeS)
        )
        return rows.operations.filter((o: any) =>
            (!filtreCategorie || o.CodeCategorie === filtreCategorie) &&
            (!filtreService || o.CodeS === filtreService) &&
            (!filtreFiliale || svcOfFiliale.has(o.CodeS))
        )
    }, [rows.operations, filtreCategorie, filtreService, filtreFiliale, services])

    const denoFiltrees = useMemo(() =>
        rows.denominations.filter((d: any) => !filtreDevise || d.CodeDevise === filtreDevise),
        [rows.denominations, filtreDevise])

    const devises = useMemo(() =>
        Array.from(new Set(rows.denominations.map((d: any) => d.CodeDevise).filter(Boolean))) as string[],
        [rows.denominations])

    const rowsByTab: Record<TabKey, any[]> = {
        operations: opsFiltrees,
        categories: rows.categories,
        origines: rows.origines,
        denominations: denoFiltrees,
        affectations: rows.affectations,
    };

    const activeTab = TABS.find(t => t.key === tab)!;

    /* ------------------------------------------------------------------ */
    /*  Rendu                                                              */
    /* ------------------------------------------------------------------ */

    const modalTitle =
        formEntite === 'OPERATION' ? (formMode === 'UPDATE' ? 'Modifier l\'opération' : 'Nouvelle opération')
        : formEntite === 'CATEGORIE' ? (formMode === 'UPDATE' ? 'Modifier la catégorie' : 'Nouvelle catégorie')
        : formEntite === 'ORIGINE' ? (formMode === 'UPDATE' ? 'Modifier l\'origine' : 'Nouvelle origine')
        : formEntite === 'DENOMINATION' ? 'Nouvelle coupure'
        : 'Affecter une opération à une filiale';

    return (
        <Spin spinning={loading} >
            <PageHeader
                title="Catalogue des opérations"
                subtitle="Opérations, catégories, origines de fonds, coupures et affectations aux filiales"
            />

            {/* Onglets (segmented) */}
            <div className="inline-flex flex-wrap items-center gap-1 p-1 rounded-xl bg-[var(--eb-bg)] border border-[var(--eb-border)] mb-3">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        type='button'
                        onClick={() => switchTab(t.key)}
                        className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors
                            ${tab === t.key
                                ? 'bg-[var(--eb-primary)] text-white shadow-sm'
                                : 'text-[var(--eb-text-muted)] hover:text-[var(--eb-text)]'}`}
                    >
                        {t.icon}
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Description de l'onglet */}
            <div className="flex items-start gap-2 mb-3 px-3 py-2.5 rounded-lg border border-[var(--eb-border)] bg-[var(--eb-primary-soft)]/40 text-sm text-[var(--eb-text)]">
                <Info size={16} className="mt-0.5 flex-shrink-0 text-[var(--eb-primary)]" />
                <span>{activeTab.hint}</span>
            </div>

            {/* Filtres + action */}
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div className="flex flex-wrap items-end gap-3">
                    {tab === 'operations' && (
                        <>
                            <div className="w-52">
                                <Select
                                    label="Catégorie"
                                    value={filtreCategorie}
                                    onChange={setFiltreCategorie}
                                    options={[
                                        { value: '', label: 'Toutes les catégories' },
                                        ...rows.categories.map((c: any) => ({ value: c.CodeCategorie, label: `${c.Libelle || c.CodeCategorie}` }))
                                    ]}
                                />
                            </div>
                            <div className="w-52">
                                <Select
                                    label="Filiale"
                                    value={filtreFiliale}
                                    onChange={(v: string) => { setFiltreFiliale(v); setFiltreService(''); }}
                                    options={[
                                        { value: '', label: 'Toutes les filiales' },
                                        ...filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
                                    ]}
                                />
                            </div>
                            <div className="w-52">
                                <Select
                                    label="Service"
                                    value={filtreService}
                                    onChange={setFiltreService}
                                    options={[
                                        { value: '', label: 'Tous les services' },
                                        ...services
                                            .filter((s: any) => !filtreFiliale || s.CodeFiliale == filtreFiliale)
                                            .map((s: any) => ({ value: s.CodeS, label: `${s.CodeS} - ${s.Libelle || ''}` }))
                                    ]}
                                />
                            </div>
                            {(filtreCategorie || filtreFiliale || filtreService) && (
                                <Button variant='ghost' size='sm' onClick={() => { setFiltreCategorie(''); setFiltreFiliale(''); setFiltreService(''); }}>
                                    Réinitialiser
                                </Button>
                            )}
                        </>
                    )}
                    {tab === 'denominations' && (
                        <div className="w-52">
                            <Select
                                label="Devise"
                                value={filtreDevise}
                                onChange={setFiltreDevise}
                                options={[
                                    { value: '', label: 'Toutes les devises' },
                                    ...devises.map(d => ({ value: d, label: d }))
                                ]}
                            />
                        </div>
                    )}
                    {tab === 'affectations' && (
                        <div className="w-56">
                            <Select
                                label="Filiale"
                                value={filtreAffectation}
                                onChange={(v: string) => { setFiltreAffectation(v); LoadTab('affectations', v); }}
                                options={[
                                    { value: '', label: 'Toutes les filiales' },
                                    ...filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
                                ]}
                            />
                        </div>
                    )}
                </div>
                <Button
                    variant='primary'
                    size='sm'
                    startIcon={<Plus size={15} />}
                    onClick={() => (tab === 'affectations' ? openAttach() : openCreate(tab))}
                >{newLabelByTab[tab]}</Button>
            </div>

            <div className="eb-table-wrap">
                <DataTable
                    rows={rowsByTab[tab]}
                    height={400}
                    columns={columnsByTab[tab]}
                    searchable
                    emptyText={emptyTextByTab[tab]}
                />
            </div>
            <TableStatusBar
                count={rowsByTab[tab].length}
                loading={loading}
                onRefresh={() => LoadTab(tab)}
            />

            {/* Drawer Create / Update / Attach */}
            <FormDrawer
                open={formOpen}
                onClose={() => setFormOpen(false)}
                title={modalTitle}
                hint={formEntite === 'OPERATION'
                    ? 'Les prérequis pilotent le formulaire client (KYC, montant, origine des fonds, billetage, RDV).'
                    : formEntite === 'FILIALE_OPERATION'
                        ? 'L\'opération ne sera proposée que dans les filiales où elle est affectée.'
                        : undefined}
                footer={
                    <>
                        <Button variant="outline" onClick={() => setFormOpen(false)}>Annuler</Button>
                        <Button variant="primary" onClick={SaveForm} loading={saving} disabled={!formValid()}>
                            {formMode === 'ATTACH' ? 'Affecter' : 'Enregistrer'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    {formEntite === 'OPERATION' && (
                        <>
                            <Input label="Code opération *" value={form.CodeOperation ?? ''} disabled={formMode === 'UPDATE'} helperText={formMode === 'UPDATE' ? 'Clé technique — non modifiable' : undefined} onChange={(e: any) => setForm({ ...form, CodeOperation: e.target.value })} />
                            <Input label="Libellé *" value={form.Libelle ?? ''} onChange={(e: any) => setForm({ ...form, Libelle: e.target.value })} />
                            <Select
                                label="Catégorie"
                                value={form.CodeCategorie ?? ''}
                                onChange={(v: string) => setForm({ ...form, CodeCategorie: v })}
                                options={withCurrentOption(
                                    [{ value: '', label: '—' }, ...rows.categories.map((c: any) => ({ value: pick(c, 'CodeCategorie', 'Code'), label: c.Libelle || pick(c, 'CodeCategorie', 'Code') }))],
                                    form.CodeCategorie
                                )}
                            />
                            <Select
                                label="Service (CodeS — route le panier vers le guichet)"
                                value={form.CodeS ?? ''}
                                onChange={(v: string) => setForm({ ...form, CodeS: v })}
                                options={withCurrentOption(
                                    [{ value: '', label: '—' }, ...services.map((s: any) => ({ value: pick(s, 'CodeS', 'Code', 'CodeService'), label: `${pick(s, 'CodeS', 'Code', 'CodeService')}${s.Libelle ? ' - ' + s.Libelle : ''}` }))],
                                    form.CodeS
                                )}
                            />
                            <div className="pt-1">
                                <div className="text-xs font-semibold uppercase tracking-wide text-[var(--eb-text-muted)] mb-1">Prérequis — pilotent le formulaire client</div>
                                <div className="divide-y divide-[var(--eb-border)] rounded-lg border border-[var(--eb-border)]">
                                    {PREREQUIS_FLAGS.map(f => (
                                        <div key={f.field} className="flex items-center justify-between gap-3 px-3 py-2.5">
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-[var(--eb-text)]">{f.label}</div>
                                                <div className="text-[11px] text-[var(--eb-text-muted)] leading-snug">{f.hint}</div>
                                            </div>
                                            <Switch
                                                checked={!!form[f.field]}
                                                onChange={(v: boolean) => setForm({ ...form, [f.field]: v })}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {formMode === 'UPDATE' && form.CodeOperation && (
                                <PiecesAttendues
                                    authHeader={authHeader}
                                    codeOperation={form.CodeOperation}
                                    canEdit={canPieces}
                                />
                            )}
                        </>
                    )}
                    {formEntite === 'CATEGORIE' && (
                        <>
                            <Input label="Code catégorie *" value={form.CodeCategorie ?? ''} disabled={formMode === 'UPDATE'} onChange={(e: any) => setForm({ ...form, CodeCategorie: e.target.value })} />
                            <Input label="Libellé *" value={form.Libelle ?? ''} onChange={(e: any) => setForm({ ...form, Libelle: e.target.value })} />
                        </>
                    )}
                    {formEntite === 'ORIGINE' && (
                        <>
                            <Input label="Code origine *" value={form.CodeOrigine ?? ''} disabled={formMode === 'UPDATE'} onChange={(e: any) => setForm({ ...form, CodeOrigine: e.target.value })} />
                            <Input label="Libellé *" value={form.Libelle ?? ''} onChange={(e: any) => setForm({ ...form, Libelle: e.target.value })} />
                        </>
                    )}
                    {formEntite === 'DENOMINATION' && (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <Input label="Code devise * (ex. XOF)" value={form.CodeDevise ?? ''} onChange={(e: any) => setForm({ ...form, CodeDevise: e.target.value })} />
                                <Select
                                    label="Type de coupure *"
                                    value={form.TypeCoupure ?? 'BILLET'}
                                    onChange={(v: string) => setForm({ ...form, TypeCoupure: v })}
                                    options={[{ value: 'BILLET', label: 'BILLET' }, { value: 'PIECE', label: 'PIÈCE' }]}
                                />
                            </div>
                            <Input label="Valeur *" type="number" value={form.Valeur ?? ''} onChange={(e: any) => setForm({ ...form, Valeur: e.target.value })} />
                        </>
                    )}
                    {formEntite === 'FILIALE_OPERATION' && (
                        <>
                            <Select
                                label="Filiale *"
                                value={form.CodeFiliale ?? ''}
                                onChange={(v: string) => setForm({ ...form, CodeFiliale: v })}
                                options={withCurrentOption(
                                    [{ value: '', label: '—' }, ...filiales.map((f: any) => ({ value: pick(f, 'CodeFiliale', 'Code'), label: `${pick(f, 'CodeFiliale', 'Code')}${f.Libelle ? ' - ' + f.Libelle : ''}` }))],
                                    form.CodeFiliale
                                )}
                            />
                            <Select
                                label="Opération *"
                                value={form.CodeOperation ?? ''}
                                onChange={(v: string) => setForm({ ...form, CodeOperation: v })}
                                options={withCurrentOption(
                                    [{ value: '', label: '—' }, ...rows.operations.map((o: any) => ({ value: o.CodeOperation, label: `${o.CodeOperation}${o.Libelle ? ' - ' + o.Libelle : ''}` }))],
                                    form.CodeOperation
                                )}
                            />
                        </>
                    )}
                </div>
            </FormDrawer>

            {/* Modal de confirmation DELETE / DETACH */}
            <Modal
                open={!!confirm}
                onClose={() => setConfirm(null)}
                title={confirm?.entite === 'FILIALE_OPERATION' ? 'Détacher l\'opération' : 'Confirmer la suppression'}
                footer={
                    <>
                        <Button variant="outline" onClick={() => setConfirm(null)}>Annuler</Button>
                        <Button variant="danger" onClick={ConfirmDelete} loading={saving} startIcon={confirm?.entite === 'FILIALE_OPERATION' ? <Unlink size={15} /> : <Trash2 size={15} />}>
                            {confirm?.entite === 'FILIALE_OPERATION' ? 'Détacher' : 'Supprimer'}
                        </Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    {confirm?.entite === 'FILIALE_OPERATION'
                        ? <>Détacher l'opération <strong>{confirm?.record?.CodeOperation}</strong> de la filiale <strong>{confirm?.record?.CodeFiliale}</strong> ?</>
                        : confirm?.entite === 'OPERATION'
                            ? <>Supprimer l'opération <strong>{confirm?.record?.CodeOperation}</strong> ? <span className="text-[var(--eb-text-muted)]">(suppression logique — l'opération est désactivée)</span></>
                            : <>Supprimer <strong>{confirm?.record?.CodeCategorie || confirm?.record?.CodeOrigine || `${confirm?.record?.Valeur} ${confirm?.record?.CodeDevise}`}</strong> ?</>
                    }
                </p>
            </Modal>
        </Spin>
    );
}

/* ------------------------------------------------------------------ */
/*  Pièces attendues par opération — POST api/Documents/               */
/*  ManageTypesOperation {Option:'SET'|'LIST', CodeOperation, Types}   */
/* ------------------------------------------------------------------ */
function PiecesAttendues(props: { authHeader: () => string; codeOperation: string; canEdit: boolean }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [types, setTypes] = useState<any[]>([])                     // types actifs (référentiel)
    const [attendues, setAttendues] = useState<Record<string, { attendue: boolean; obligatoire: boolean }>>({})

    useEffect(() => {
        loadAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.codeOperation])

    function loadAll() {
        setLoading(true)
        const pTypes = axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Documents.TypesDocuments,
            headers: { 'Accept': 'text/plain', 'Authorization': props.authHeader() },
        }).then((response: any) => {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value
            return res.StatutCode == 200 ? (res.Result || []) : []
        }).catch(() => [])

        const pAttendues = axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Documents.ManageTypesOperation,
            headers: { 'Content-Type': 'application/json', 'Authorization': props.authHeader() },
            data: Crypter.encrypt(JSON.stringify({ Option: 'LIST', CodeOperation: props.codeOperation }))
        }).then((response: any) => {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value
            return res.StatutCode == 200 ? (res.Result || []) : []
        }).catch(() => [])

        Promise.all([pTypes, pAttendues])
            .then(([actifs, attendus]) => {
                const map: Record<string, { attendue: boolean; obligatoire: boolean }> = {}
                for (const t of actifs) map[t.CodeTypeDoc] = { attendue: false, obligatoire: false }
                // Ordre : les attendus en premier selon leur Ordre, puis les autres types
                const ordonnes = [...attendus].sort((a: any, b: any) => (a.Ordre ?? 99) - (b.Ordre ?? 99))
                ordonnes.forEach((t: any) => {
                    if (t.CodeTypeDoc) map[t.CodeTypeDoc] = { attendue: true, obligatoire: !!t.Obligatoire }
                })
                // Types attendus qui ne sont plus dans le référentiel actif → ajoutés en fin
                const list = [
                    ...ordonnes.map((t: any) => ({
                        CodeTypeDoc: t.CodeTypeDoc,
                        Libelle: t.LibelleType || actifs.find((a: any) => a.CodeTypeDoc === t.CodeTypeDoc)?.Libelle || t.CodeTypeDoc,
                    })),
                    ...actifs.filter((a: any) => !ordonnes.some((o: any) => o.CodeTypeDoc === a.CodeTypeDoc)),
                ]
                setTypes(list)
                setAttendues(map)
            })
            .finally(() => setLoading(false));
    }

    function toggle(code: string, attendue: boolean) {
        setAttendues(prev => ({ ...prev, [code]: { attendue, obligatoire: attendue ? prev[code]?.obligatoire ?? false : false } }))
    }

    function handleSave() {
        const typesPayload = types
            .filter(t => attendues[t.CodeTypeDoc]?.attendue)
            .map((t, i) => ({ CodeTypeDoc: t.CodeTypeDoc, Obligatoire: !!attendues[t.CodeTypeDoc]?.obligatoire, Ordre: i + 1 }))
        setSaving(true)
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Documents.ManageTypesOperation,
            headers: { 'Content-Type': 'application/json', 'Authorization': props.authHeader() },
            data: Crypter.encrypt(JSON.stringify({ Option: 'SET', CodeOperation: props.codeOperation, Types: typesPayload }))
        })
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) alert.success(res.Message)
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    const nbAttendues = Object.values(attendues).filter(a => a.attendue).length

    return (
        <div className="pt-1">
            <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold uppercase tracking-wide text-[var(--eb-text-muted)] flex items-center gap-1.5">
                    <Paperclip size={13} />
                    Pièces attendues — déposées par le client
                </div>
                {nbAttendues > 0 && <Badge color="violet">{nbAttendues}</Badge>}
            </div>
            <Spin spinning={loading}>
                <div className="divide-y divide-[var(--eb-border)] rounded-lg border border-[var(--eb-border)]">
                    {types.length === 0 && !loading && (
                        <div className="px-3 py-3 text-xs text-[var(--eb-text-muted)]">Aucun type de pièce actif — créez-en dans « Documents › Types de pièces ».</div>
                    )}
                    {types.map(t => {
                        const a = attendues[t.CodeTypeDoc] || { attendue: false, obligatoire: false }
                        return (
                            <div key={t.CodeTypeDoc} className="flex items-center justify-between gap-3 px-3 py-2">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-[var(--eb-text)]">{t.Libelle || t.CodeTypeDoc}</div>
                                    <div className="text-[11px] text-[var(--eb-text-muted)] font-mono">{t.CodeTypeDoc}</div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    {a.attendue && props.canEdit && (
                                        <label className="flex items-center gap-1.5 text-xs text-[var(--eb-text-muted)]">
                                            <Checkbox
                                                checked={a.obligatoire}
                                                onChange={(v: boolean) => setAttendues(prev => ({ ...prev, [t.CodeTypeDoc]: { ...prev[t.CodeTypeDoc], obligatoire: v } }))}
                                            />
                                            Obligatoire
                                        </label>
                                    )}
                                    {a.attendue && !props.canEdit && (
                                        <Badge color={a.obligatoire ? 'amber' : 'gray'}>{a.obligatoire ? 'Obligatoire' : 'Facultatif'}</Badge>
                                    )}
                                    <Switch
                                        checked={a.attendue}
                                        disabled={!props.canEdit}
                                        onChange={(v: boolean) => toggle(t.CodeTypeDoc, v)}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
                {props.canEdit && types.length > 0 && (
                    <div className="mt-2 flex justify-end">
                        <Button variant='secondary' size='sm' loading={saving} startIcon={<Save size={14} />} onClick={handleSave}>
                            Enregistrer les pièces attendues
                        </Button>
                    </div>
                )}
            </Spin>
        </div>
    );
}
